// The Meta-ad tin deal's unlock (deals 0.9.0; plan "Tin deal with the
// Meta-ad unlock", Michael 2026-10-09/10). The deals client asks the server
// for a pass (?hd=<code>) and keeps it in localStorage (UNLOCK_KEYS); this
// reads that, so the page prices the $60 tin deal only while a pass is live
// - the server decides the real price at checkout.
//
//   dealContext()   priceBasket's context: { unlocks: ['meta-ad'] } while live
//   unlockState()   'live', 'expired', 'used', 'none' (never unlocked),
//                   'pending' (an ad code in the address, being checked) or
//                   'failed' (no answer in WAIT: the request failed, timed
//                   out or was refused - the client only logs that)
//   settled()       the state once it isn't 'pending' (at most WAIT)
//   passOnCart()    resolves once the cart holds the pass the client stored
//                   (it writes the cart after its event), at most 4 s
//   passReady()     resolves at once, or - while an ad code is being checked
//                   or a new pass is being written to the cart - once that is
//                   done. Every Add to Cart waits for it (below).
//
// Fires 'deal:unlock' on document whenever the state may have changed (the
// client's deals:unlock / deals:unlock-ended, or the pass running out while
// the page is open). Also keeps the basket's "offer has expired" line
// (snippets/deal-ended-line) right: shown for an expired or used pass while
// small tins are in the basket.

import { UNLOCK_KEYS } from '@deals/client'
import { refreshCart } from '@split-infinity/cardo/cart'
import { swapRegions, sectionIdOf } from '@split-infinity/cardo/swap'

const PARAM = 'hd'
const WAIT = 6000
const adCode = () => new URLSearchParams(location.search).get(PARAM)
let answered = false // the client has said live or ended on this page
let failed = false // an ad code got no answer in WAIT

function read(key) {
  try {
    return JSON.parse(localStorage.getItem(key) ?? 'null')
  } catch {
    return null
  }
}

const future = (iso) => Boolean(iso) && Date.parse(iso) > Date.now()

/** The cart's pass end (cardo:cart), for a pass made on another device's localStorage-less visit. */
let cartUntil = null
// What the client said on this page. It fires its events before it stores
// (or drops) the pass, so these come before localStorage.
let eventUntil = null // deals:unlock's pass end
let eventEnded = null // deals:unlock-ended's state

export function unlockState() {
  if (eventEnded) return eventEnded
  const held = read(UNLOCK_KEYS.pass)
  if (future(held?.until) || future(cartUntil) || future(eventUntil)) return 'live'
  const ended = read(UNLOCK_KEYS.ended)
  if (!answered && adCode()) return failed ? 'failed' : 'pending'
  if (ended?.state === 'expired' || ended?.state === 'used') return ended.state
  if (held?.until || cartUntil) return 'expired'
  return 'none'
}

export const dealContext = () => (unlockState() === 'live' ? { unlocks: ['meta-ad'] } : {})

function endedLines(root = document) {
  const state = unlockState()
  const ended = state === 'expired' || state === 'used'
  root.querySelectorAll('[data-deal-ended-line]').forEach((el) => {
    el.hidden = !(ended && Number(el.dataset.dealTins) > 0)
    el.querySelectorAll('[data-deal-ended-text]').forEach((t) => {
      t.hidden = t.dataset.dealEndedText !== (state === 'used' ? 'used' : 'expired')
    })
  })
}

// Once the client has answered, the ad code leaves the address: Back or a
// reload after an order would otherwise ask for a fresh pass, and only a new
// ad tap should (Michael 2026-10-10). Kept after no answer, so Try again
// (a reload) asks again.
function dropCode() {
  const url = new URL(location.href)
  if (!url.searchParams.has(PARAM)) return
  url.searchParams.delete(PARAM)
  try {
    history.replaceState(history.state, '', url.pathname + url.search + url.hash)
  } catch {}
}

function changed() {
  endedLines()
  document.dispatchEvent(new CustomEvent('deal:unlock', { detail: { state: unlockState() } }))
}

export const settled = () =>
  new Promise((resolve) => {
    const check = () => {
      const state = unlockState()
      if (state === 'pending') return false
      document.removeEventListener('deal:unlock', check)
      resolve(state)
      return true
    }
    if (!check()) document.addEventListener('deal:unlock', check)
  })

// Each read gives up after 2 s, so a hung request can't hold Add (2026-10-11 review).
const getCart = () =>
  fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store', signal: AbortSignal.timeout?.(2000) }).then((r) => r.json())

// The cart's pass matching the stored one (or none). Adding before the
// client's cart write is done raced it: on a first visit Shopify made two
// carts, and the basket showed $25 tins while the page said $60 (review
// 2026-10-10).
export async function passOnCart() {
  const want = () => read(UNLOCK_KEYS.pass)?.pass ?? ''
  for (const end = Date.now() + 4000; Date.now() < end; ) {
    const cart = await getCart().catch(() => null)
    if (cart && (cart.attributes?._deals_unlock ?? '') === want()) return cart
    await new Promise((r) => setTimeout(r, 300))
  }
  return null
}

// Adding before the pass is on the cart lost it: on arrival from the ad, a
// quick tap on Add put the tin in a cart without the pass, so the page said
// $60 and checkout charged $75 (review 2026-10-11). Until the ad code is
// answered and the pass written, every Add to Cart waits (at most WAIT + 4 s).
// Only with deals on: without the client no answer comes (2026-10-11 review).
let ready = Promise.resolve()
let waiting = false
/** Add to Cart waits for `p`, the newest wait. */
function hold(p) {
  ready = p
  waiting = true
  p.finally(() => {
    if (ready === p) waiting = false
  })
}
/** Resolves once the newest wait is done, not one a later pass replaced. */
async function newest() {
  let p
  do {
    p = ready
    await p
  } while (p !== ready)
}
export const passReady = () => ready
if (document.documentElement.dataset.dealsApi && adCode()) {
  hold(settled().then((s) => (s === 'live' ? passOnCart() : null)))
}
// Capture phase: before cardo's own Add to Cart handler.
document.addEventListener(
  'submit',
  (e) => {
    const form = e.target
    if (!waiting || !(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) return
    e.preventDefault()
    e.stopImmediatePropagation()
    // A button already disabled stays so; one outside the form (form=) still submits it.
    const button = e.submitter
    const ours = button && !button.disabled
    if (ours) button.setAttribute('disabled', '')
    newest().then(() => {
      if (ours) button.removeAttribute('disabled')
      form.requestSubmit(button?.form === form ? button : undefined)
    })
  },
  true
)

// The basket's prices come from the cart's pass (snippets/deals-rules), so
// once the pass arrives, ends or runs out, the basket is drawn again. Always:
// another cart write (Pulse's) can announce the new cart without redrawing.
async function redraw() {
  await passOnCart()
  redrawBasket().catch(() => {})
}

/**
 * The basket drawn again from the cart as it is now, then cardo:cart. Not
 * cardo 0.9.1's refreshCart: it asks for the sections with Accept:
 * application/json, and on a page, a collection or the cart Shopify answers
 * with that page's own JSON instead, so nothing was redrawn (2026-10-10).
 */
export async function redrawBasket() {
  const cartPage = document.querySelector('[data-cart-page]')
  const ids = ['drawer-cart', cartPage && sectionIdOf(cartPage)].filter(Boolean)
  const res = await fetch(`${location.pathname}?sections=${ids.join(',')}`, { cache: 'no-store' })
  if (res.ok) {
    for (const [id, html] of Object.entries(await res.json())) {
      const root = document.getElementById(`shopify-section-${id}`)
      if (root && html) swapRegions(root, html)
    }
  }
  return refreshCart({ sections: false })
}

// The pass runs out while the page is open: tell the page then.
let timer = null
function watchEnd() {
  clearTimeout(timer)
  const ends = [read(UNLOCK_KEYS.pass)?.until, cartUntil, eventUntil].filter(future).map(Date.parse)
  if (ends.length) timer = setTimeout(() => (changed(), redraw()), Math.min(Math.max(...ends) - Date.now() + 1000, 2 ** 31 - 1))
}

document.addEventListener('deals:unlock', (e) => {
  answered = true
  dropCode()
  eventUntil = e.detail?.until ?? null
  eventEnded = null
  hold(passOnCart())
  watchEnd()
  changed()
  redraw()
})
document.addEventListener('deals:unlock-ended', (e) => {
  answered = true
  dropCode()
  cartUntil = null
  eventUntil = null
  eventEnded = ['expired', 'used'].includes(e.detail?.state) ? e.detail.state : 'none'
  changed()
  redraw()
})
if (adCode()) {
  setTimeout(() => {
    if (answered) return
    failed = true
    changed()
  }, WAIT)
}
// Another tab got a pass, or saw it end (2026-10-11 review): what this page
// heard from the client and the cart is out of date. Read both again.
window.addEventListener('storage', (e) => {
  if (e.key !== null && e.key !== UNLOCK_KEYS.pass && e.key !== UNLOCK_KEYS.ended) return
  eventUntil = null
  eventEnded = null
  cartUntil = null
  watchEnd()
  changed()
  redrawBasket().catch(() => {})
})
// Capture phase: before the deal prices and the deal page read the cart.
document.addEventListener(
  'cardo:cart',
  (e) => {
    const until = e.detail.cart?.attributes?._deals_unlock_until || null
    if (until === cartUntil) return
    cartUntil = until
    watchEnd()
  },
  true
)
watchEnd()
// The basket is redrawn by cardo after every change.
document.addEventListener('cardo:render', (e) => endedLines(e.detail?.root ?? document))
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => endedLines(), { once: true })
} else {
  endedLines()
}
